import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

const TIER_LABELS = {
  platform: "Platform", florida_transfer: "Florida Transfer",
  state: "Florida Transfer", selective_elite: "Selective & Elite",
  nationwide: "Selective & Elite",
};

export default function AdminClients() {
  const [q, setQ] = useState("");
  const [tier, setTier] = useState("");
  const [exemplarsOnly, setExemplarsOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: 25 });

  useEffect(() => { (async () => {
    try {
      const params = new URLSearchParams({ page: String(page), page_size: "50" });
      if (q) params.set("q", q);
      if (tier) params.set("tier", tier);
      const r = await api.get(`/admin/clients?${params.toString()}`);
      setData(r.data);
    } catch {}
  })(); }, [q, tier, page]);

  const items = exemplarsOnly ? data.items.filter((c) => c.is_exemplar) : data.items;

  return (
    <div className="space-y-6" data-testid="admin-clients-page">
      <header>
        <h1 className="font-serif text-3xl text-ink tracking-tight">Clients</h1>
        <p className="text-ink-soft text-sm mt-1">{data.total} total</p>
      </header>

      <div className="flex flex-wrap gap-3 items-center">
        <input
          value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
          placeholder="Search by name or email"
          className="bg-bone border border-stone/40 px-3 py-2 text-sm w-72 focus:outline-none focus:border-ivy"
          data-testid="clients-search"
        />
        <select value={tier} onChange={(e) => { setTier(e.target.value); setPage(1); }}
          className="bg-bone border border-stone/40 px-3 py-2 text-sm" data-testid="clients-tier-filter">
          <option value="">All tiers</option>
          <option value="platform">Platform</option>
          <option value="florida_transfer">Florida Transfer</option>
          <option value="selective_elite">Selective &amp; Elite</option>
          <option value="audit_only">Audit-only</option>
        </select>
        <button
          onClick={() => setExemplarsOnly(!exemplarsOnly)}
          className={`px-3 py-2 text-[11px] uppercase tracking-[0.18em] border transition-colors ${exemplarsOnly ? "bg-brass border-brass text-bone" : "bg-bone border-stone/40 text-stone hover:text-ink"}`}
          data-testid="clients-exemplars-filter"
        >
          <span className="mr-1">◆</span> Exemplars only
        </button>
      </div>

      <div className="border border-stone/40 bg-bone overflow-hidden" data-testid="clients-table">
        <div className="grid grid-cols-12 gap-3 px-4 py-2.5 bg-bone-dim border-b border-stone/40 text-[10px] uppercase tracking-[0.18em] text-stone">
          <div className="col-span-3">Name</div>
          <div className="col-span-3">Email</div>
          <div className="col-span-2">Tier</div>
          <div className="col-span-2">Target schools</div>
          <div className="col-span-1">GPA</div>
          <div className="col-span-1 text-center">◆</div>
        </div>
        {items.length === 0 ? (
          <div className="px-4 py-6 text-sm text-ink-soft">No clients match.</div>
        ) : items.map((c) => (
          <Link key={c.user_id} to={`/admin/clients/${c.user_id}`}
            className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-stone/20 hover:bg-bone-dim transition-colors items-baseline"
            data-testid={`client-row-${c.user_id}`}>
            <div className="col-span-3 text-sm text-ink truncate">{c.name || "—"}</div>
            <div className="col-span-3 text-xs text-ink-soft truncate font-mono">{c.email}</div>
            <div className="col-span-2 text-xs">
              <span className="inline-block border border-stone/40 px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] text-ivy">
                {TIER_LABELS[c.tier] || (c.audit_pending ? "Audit-only" : "—")}
              </span>
            </div>
            <div className="col-span-2 text-xs text-ink-soft truncate">{(c.target_schools || []).join(", ") || "—"}</div>
            <div className="col-span-1 text-xs text-ink tabular-nums">{c.current_gpa ?? "—"}</div>
            <div className="col-span-1 text-center text-brass text-lg" data-testid={`client-exemplar-${c.user_id}`}>
              {c.is_exemplar ? "◆" : ""}
            </div>
          </Link>
        ))}
      </div>

      <div className="flex justify-between items-center text-xs text-stone">
        <span>Page {data.page} of {Math.max(1, Math.ceil(data.total / data.page_size))}</span>
        <div className="flex gap-3">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="border border-stone/40 px-3 py-1 disabled:opacity-40">Prev</button>
          <button disabled={page * data.page_size >= data.total} onClick={() => setPage(page + 1)} className="border border-stone/40 px-3 py-1 disabled:opacity-40">Next</button>
        </div>
      </div>
    </div>
  );
}
